import { Grid, IconButton, InputAdornment } from "@material-ui/core";
import ClearIcon from "@material-ui/icons/Clear";
import DeleteIcon from "@material-ui/icons/Delete";
import moment from "moment-timezone";
import { useState } from "react";
import { AutocompleteInput, Button, CheckboxGroupInput, CloneButton, Confirm, Create, Datagrid, DateInput, DeleteButton, Edit, EditButton, Filter, FunctionField, List, ReferenceInput, required, SaveButton, SelectInput, SimpleForm, TextField, TextInput, Toolbar, useDataProvider, useNotify, useRecordContext } from "react-admin";
import { useForm, useFormState } from "react-final-form";
import { MyDateField } from "../../MyDateField";
import { MyDateTimeInput } from "../../MyDateTimeInput";
import dataProvider from "../../dataProvider";

const statusChoices = [
  { id: "preview", name: "Preview" },
  { id: "live", name: "Live" },
  { id: "end", name: "End" },
];

const weekdayNames = {
  0: "Sun",
  1: "Mon",
  2: "Tue",
  3: "Wed",
  4: "Thu",
  5: "Fri",
  6: "Sat",
};

const repeatDayChoices = [
  { id: 1, name: "Mon" },
  { id: 2, name: "Tue" },
  { id: 3, name: "Wed" },
  { id: 4, name: "Thu" },
  { id: 5, name: "Fri" },
  { id: 6, name: "Sat" },
  { id: 0, name: "Sun" },
];

const EpgModificationFilter = (props) => (
  <Filter {...props}>
    <TextInput label="OTT Ch Code" source="network_code" alwaysOn />
    <TextInput label="Programme ID" source="programme_id" alwaysOn />
    <SelectInput label="Status" source="status" choices={statusChoices} alwaysOn />
  </Filter>
);

const AutoCompleteProgramme = ({ disabled }) => {
  const form = useForm();
  const provider = useDataProvider();

  const fillProgrammeFields = async (programmeId) => {
    if (!programmeId) {
      return;
    }

    const result = await provider.getProgrammeByProgrammeId(parseInt(programmeId));
    if (result?.data?.name_tc) {
      form.change("programme_id", programmeId);
      form.change("programme_title_tc", result.data.name_tc);
      form.change("programme_title_en", result.data.name_en);
    }
  };

  return (
    <ReferenceInput
      label="Search Programme Name"
      source="programme_autocomplete"
      reference="programmes-autocomplete"
      onChange={fillProgrammeFields}
      enableGetChoices={({ name_tc }) => Boolean(name_tc)}
      filterToQuery={(searchText) => (searchText ? { name_tc: searchText } : {})}
      disabled={disabled}
    >
      <AutocompleteInput optionText="name_tc" optionValue="programme_id" disabled={disabled} fullWidth />
    </ReferenceInput>
  );
};

const validateEpgModification = (values) => {
  const errors = {};
  const repeatDays = values.repeat_weekday || [];

  if (repeatDays.length) {
    if (!values.repeat_start_date) {
      errors.repeat_start_date = "Repeat Start day is required when Repeat Days are selected";
    }
    if (!values.repeat_end_date) {
      errors.repeat_end_date = "Repeat End day is required when Repeat Days are selected";
    }
  }

  if (values.start_time && values.end_time && moment(values.end_time).isBefore(values.start_time)) {
    errors.end_time = "End time must be after Start time";
  }

  if (values.repeat_start_date && values.start_time && moment.tz(values.repeat_start_date, "Asia/Hong_Kong").isBefore(moment.tz(values.start_time, "Asia/Hong_Kong").startOf("day"))) {
    errors.repeat_start_date = "Repeat Start day must be on or after the Start time day";
  }

  if (values.repeat_start_date && values.repeat_end_date && moment.tz(values.repeat_end_date, "Asia/Hong_Kong").isBefore(moment.tz(values.repeat_start_date, "Asia/Hong_Kong"))) {
    errors.repeat_end_date = "Repeat End day must be on or after the Repeat Start day";
  }

  return errors;
};

const EpgModificationCreateToolbar = (props) => (
  <Toolbar {...props}>
    <SaveButton />
  </Toolbar>
);

const ProgrammeIdInput = ({ disabled }) => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });

  return (
    <TextInput
      label="Programme ID"
      source="programme_id"
      validate={required()}
      disabled
      InputProps={{
        endAdornment: values.programme_id && !disabled ? (
          <InputAdornment position="end">
            <IconButton aria-label="Clear programme ID" onClick={() => form.change("programme_id", "")} edge="end" style={{ color: "red" }}>
              <ClearIcon />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
      fullWidth
    />
  );
};

const EpgModificationForm = ({ disableEndedRecord = false }) => {
  const record = useRecordContext();
  const disabled = disableEndedRecord && getRecordStatus(record) === "end";

  return (
    <Grid container spacing={1} fullWidth>
      <Grid item xs={12} md={4}>
        <TextInput label="OTT Ch Code" source="network_code" validate={required()} disabled={disabled} fullWidth />
      </Grid>
      <Grid item xs={12} md={4}>
        <AutoCompleteProgramme disabled={disabled} />
      </Grid>
      <Grid item xs={12} md={4}>
        <ProgrammeIdInput disabled={disabled} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput label="Bonus VOD title in TC" source="programme_title_tc" validate={required()} disabled={disabled} fullWidth />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput label="Bonus VOD title in EN" source="programme_title_en" validate={required()} disabled={disabled} fullWidth />
      </Grid>
      <Grid item xs={12} md={6}>
        <MyDateTimeInput label="Start time" source="start_time" validate={required()} disabled={disabled} fullWidth />
      </Grid>
      <Grid item xs={12} md={6}>
        <MyDateTimeInput label="End time" source="end_time" validate={required()} disabled={disabled} fullWidth />
      </Grid>
      <Grid item xs={12}>
        <CheckboxGroupInput label="Repeat days" source="repeat_weekday" choices={repeatDayChoices} disabled={disabled} />
      </Grid>
      <Grid item xs={12} md={6}>
        <DateInput label="Repeat Start day" source="repeat_start_date" disabled={disabled} fullWidth />
      </Grid>
      <Grid item xs={12} md={6}>
        <DateInput label="Repeat End day" source="repeat_end_date" disabled={disabled} fullWidth />
      </Grid>
    </Grid>
  );
};

const getRecordStatus = (record) => String(record?.status || "").toLowerCase();
const getStatusDisplayName = (record) => statusChoices.find(({ id }) => id === getRecordStatus(record))?.name || record.status;
const deleteButtonStyle = { color: "red" };

const EpgModificationEditToolbar = (props) => (
  <Toolbar {...props}>
    <SaveButton disabled={getRecordStatus(props.record) === "end"} />
  </Toolbar>
);

const EpgModificationActionButton = () => {
  const [showConfirm, setShowConfirm] = useState(false);
  const record = useRecordContext();
  const notify = useNotify();
  const status = getRecordStatus(record);

  if (status === "preview") {
    return <DeleteButton record={record} resource="epg-modification" basePath="/epg-modification" undoable={false} style={deleteButtonStyle} />;
  }

  if (status !== "live") {
    return null;
  }

  return (
    <>
      <Button label="End" onClick={() => setShowConfirm(true)} style={deleteButtonStyle}>
        <DeleteIcon />
      </Button>
      <Confirm
        isOpen={showConfirm}
        title="Are you sure you want to end this item?"
        onConfirm={async () => {
          try {
            const now = moment.tz("Asia/Hong_Kong").toISOString();
            await dataProvider.update("epg-modification", {
              id: record.id,
              data: {
                ...record,
                end_time: now,
                repeat_end_date: now,
              },
            });
            window.location.reload(true);
          } catch (error) {
            notify(error.message.toString(), "warning");
          }

          setShowConfirm(false);
        }}
        onClose={() => setShowConfirm(false)}
      />
    </>
  );
};

export const EpgModificationList = (props) => {
  return (
    <List {...props} filters={<EpgModificationFilter />} bulkActionButtons={false} exporter={false} sort={{ field: "created_at", order: "DESC" }}>
      <Datagrid>
        <TextField label="OTT Ch Code" source="network_code" sortable={false} />
        <TextField label="Programme ID" source="programme_id" sortable={false} />
        <TextField label="Bonus VOD title in TC" source="programme_title_tc" sortable={false} />
        <TextField label="Bonus VOD title in EN" source="programme_title_en" sortable={false} />
        <FunctionField
          label="Status"
          render={(record) => <span style={{ color: getRecordStatus(record) === "live" ? "red" : "inherit" }}>{getStatusDisplayName(record)}</span>}
          sortable={false}
        />
        <MyDateField label="Start Time" source="start_time" showTime sortable={false} />
        <MyDateField label="End Time" source="end_time" showTime sortable={false} />
        <FunctionField label="Repeat Days" render={(record) => (record.repeat_weekday || []).map((weekday) => weekdayNames[weekday] || weekday).join(", ")} sortable={false} />
        <MyDateField label="Repeat Start date" source="repeat_start_date" sortable={false} />
        <MyDateField label="Repeat End date" source="repeat_end_date" sortable={false} />
        <EditButton sortable={false} />
        <CloneButton sortable={false} />
        <EpgModificationActionButton />
      </Datagrid>
    </List>
  );
};

export const EpgModificationCreate = (props) => {
  const notify = useNotify();
  const onFailure = (error) => {
    const message = error?.body?.message || error?.message || error;
    notify(Array.isArray(message) ? message.join("\n") : message.toString(), "warning");
  };

  return (
    <Create {...props} onFailure={onFailure} mutationMode="pessimistic">
      <SimpleForm toolbar={<EpgModificationCreateToolbar />} validate={validateEpgModification} redirect="list">
        <EpgModificationForm />
      </SimpleForm>
    </Create>
  );
};

export const EpgModificationEdit = (props) => {
  const notify = useNotify();
  const onFailure = (error) => {
    const message = error?.body?.message || error?.message || error;
    notify(Array.isArray(message) ? message.join("\n") : message.toString(), "warning");
  };

  return (
    <Edit {...props} onFailure={onFailure} mutationMode="pessimistic">
      <SimpleForm toolbar={<EpgModificationEditToolbar />} validate={validateEpgModification} redirect="list">
        <EpgModificationForm disableEndedRecord />
      </SimpleForm>
    </Edit>
  );
};
